import React, { useState } from 'react';
import axios from 'axios';
import { contractorAuth } from '../lib/contractorAuth';
import './HoursLogger.css';

const HoursLogger = ({ workOrderId, allocatedHours, loggedHours, onHoursLogged }) => {
  const [formData, setFormData] = useState({
    dateWorked: new Date().toISOString().split('T')[0],
    hoursLogged: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const remainingHours = allocatedHours - loggedHours;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const hours = parseFloat(formData.hoursLogged);
    
    if (!formData.hoursLogged || isNaN(hours) || hours <= 0) {
      setError('Hours must be a positive number');
      return;
    }
    
    // Only when there IS an allocation to run out of.
    //
    // A job with no allocated hours has remainingHours of 0, so this refused
    // every entry with "Cannot log more than 0 hours remaining" - work that
    // was genuinely done, blocked by a budget nobody set. WO-JAR-2026-0003 is
    // exactly that job. An over-run on a job that does have an allocation is
    // still worth stopping at the form.
    if (allocatedHours > 0 && hours > remainingHours) {
      setError(`Cannot log more than ${remainingHours} hours remaining`);
      return;
    }
    
    if (!formData.dateWorked) {
      setError('Date worked is required');
      return;
    }
    
    const workDate = new Date(formData.dateWorked);
    if (workDate > new Date()) {
      setError('Date cannot be in the future');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      
      // POST /api/hours-logs DOES NOT EXIST. It never has: routes/analytics.js
      // defines a GET on that path and nothing defines a POST, so this form
      // posted into the not-found mount and every submission answered 404.
      // The route that records time is POST /api/work-orders/:id/hours, gated
      // by workOrderAccess, which is also what TimeSheet has always used -
      // this component was the odd one out.
      //
      // With the contractor's own token when there is one: this form is on the
      // work order detail page, which a contractor can reach, and
      // lib/apiClient attaches only the staff Entra token. Undefined for
      // staff, so their request picks that up from the interceptor as before.
      await axios.post(
        `${API_BASE}/work-orders/${workOrderId}/hours`,
        {
          hours_logged: hours,
          date_worked: formData.dateWorked,
          description: formData.description || 'Hours logged'
        },
        contractorAuth()
      );

      setSuccess(true);
      setFormData({
        dateWorked: new Date().toISOString().split('T')[0],
        hoursLogged: '',
        description: ''
      });
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
      
      // Notify parent to refresh
      if (onHoursLogged) {
        onHoursLogged();
      }
      
    } catch (err) {
      console.error('Error logging hours:', err);
      setError(err.response?.data?.error || 'Failed to log hours');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hours-logger">
      {!showForm ? (
        <button 
          className="hours-logger-btn"
          onClick={() => setShowForm(true)}
          disabled={remainingHours <= 0}
          title={remainingHours <= 0 ? 'All hours allocated' : 'Log work hours'}
        >
          + Log Hours
        </button>
      ) : (
        <div className="hours-logger-form-container">
          <div className="hours-logger-header">
            <h4>Log Work Hours</h4>
            <p className="hours-remaining">
              Remaining: <strong>{remainingHours.toFixed(1)}</strong> hours
            </p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">Hours logged successfully!</div>}

          <form onSubmit={handleSubmit} className="hours-logger-form">
            <div className="form-group">
              <label htmlFor="dateWorked">Date Worked *</label>
              <input
                type="date"
                id="dateWorked"
                name="dateWorked"
                value={formData.dateWorked}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="hoursLogged">
                Hours Logged * 
                <span className="max-hint">(max: {remainingHours.toFixed(1)})</span>
              </label>
              <input
                type="number"
                id="hoursLogged"
                name="hoursLogged"
                step="0.5"
                min="0.5"
                max={remainingHours}
                value={formData.hoursLogged}
                onChange={handleChange}
                placeholder="e.g., 8.0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">What did you work on?</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the work completed..."
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Log Hours'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default HoursLogger;
